/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: Real-world use cases section with accordion-style tabs and video player
 */
import Image from 'next/image';
import React, { useMemo, useRef, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

interface UseCase {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  posterUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'ecommerce',
    title: 'E-commerce & Online Selling',
    description:
      'Online stores and sellers can turn product photos, descriptions, or feature lists into short product videos. AI-generated visuals highlight key details, usage scenarios, and benefits more clearly than static images. These videos work well for product pages, ads, and social media, helping shoppers understand products faster and feel more confident before buying.',
    videoUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-ecommerce.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-video-generator/usecase-ecommerce-poster.webp',
  },
  {
    id: 'education',
    title: 'Education & Training',
    description:
      'Teachers, coaches, and course creators can turn scripts, notes, or diagrams into easy-to-follow explainer videos. Animated scenes break down complex topics and make lessons clearer. As learning becomes more visual, students stay engaged for longer.',
    videoUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-education.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-video-generator/usecase-education-poster.webp',
  },
  {
    id: 'creative',
    title: 'Creative Storytelling',
    description:
      'Authors, artists, and worldbuilders can bring characters and scenes to life with AI-generated video. Consistent visuals across clips make story building smoother. Beyond that, storyboards and concept videos come together with less effort. Early ideas become easier to shape and share.',
    videoUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-social.mp4',
    posterUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-social-poster.webp',
  },
  {
    id: 'business',
    title: 'Business Teams',
    description:
      'Companies can turn announcements, product updates, or internal messages into clean, polished videos. Animated visuals make information easier to follow than long documents or static slides. Teams communicate updates with less time spent preparing materials.',
    videoUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-marketing.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-video-generator/usecase-marketing-poster.webp',
  },
  {
    id: 'personal',
    title: 'Personal Projects',
    description:
      "Anyone can turn photos, prompts, or simple ideas into fun videos for everyday use. People often make birthday messages, travel recaps, or small celebration clips this way. It's an easy way to create gifts or share moments online. No editing skills are needed to get a smooth result.",
    videoUrl: 'https://assets.falcocut.ai/ai-video-generator/usecase-corporate.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-video-generator/usecase-corporate-poster.webp',
  },
];

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const activeUseCase = useCases[activeIndex];

  // 预加载所有封面图片
  const posterUrls = useMemo(() => useCases.map((uc) => uc.posterUrl), []);
  useImagePreload(posterUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handlePlayClick = () => {
    if (videoRef.current && activeUseCase.videoUrl) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Top Use Cases for AI-Generated Videos
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Whether you&apos;re explaining a concept, building a story, or sharing a
          personal moment, FalcoCut transforms your ideas into expressive videos. See how
          marketers, educators, and creative teams use AI to scale production, stay
          consistent, and work with more freedom.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Video */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-black aspect-[4/3]">
              {/* 封面图片 - 始终渲染，通过 opacity 控制显隐实现平滑过渡 */}
              <Image
                src={activeUseCase.posterUrl}
                alt={activeUseCase.title}
                fill
                className={`object-cover transition-opacity duration-300 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              {/* 视频元素 - 始终渲染，通过 opacity 控制显隐实现平滑过渡 */}
              <video
                ref={videoRef}
                key={activeUseCase.videoUrl}
                src={activeUseCase.videoUrl}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
                onEnded={handleVideoEnded}
                onClick={handleVideoClick}
                playsInline
              />
              {/* Play Button Overlay - 带过渡效果 */}
              <button
                type="button"
                onClick={handlePlayClick}
                className={`absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-all duration-300 cursor-pointer z-10 ${isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                aria-label="Play video"
              >
                <PlayButton />
              </button>
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
